import * as React from 'react';
import avatarFB from '@/assets/images/avatar-fb.png';
import avatarIG from '@/assets/images/avatar-ig.png';
import avatarLI from '@/assets/images/avatar-linkedin.png';
import { LinkIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn, isMergedChannel } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const AvatarProfile = React.forwardRef(
  (
    {
      className,
      name,
      username,
      followers,
      following,
      isVerified,
      bio,
      channel,
      plan,
      phone,
      email,
      external_url, // Add external_url prop
      linkedin_profile_link,
      ...props
    },
    ref
  ) => {
    // Function to format phone number
    const formatPhoneNumber = (phoneNumber) => {
      if (!phoneNumber) return '';

      // Remove all non-digit characters
      const cleaned = phoneNumber.replace(/\D/g, '');

      // Handle the specific format like +14374527527
      if (cleaned.length === 11 && cleaned[0] === '1') {
        // US/Canada format: +1 (437) 452-7527
        return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(
          4,
          7
        )}-${cleaned.slice(7)}`;
      } else if (cleaned.length === 10) {
        // US format without country code: (437) 452-7527
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
          6
        )}`;
      } else if (cleaned.length > 11) {
        // International format: +XX XXX XXX XXXX
        const countryCode = cleaned.slice(0, -10);
        const areaCode = cleaned.slice(-10, -7);
        const firstPart = cleaned.slice(-7, -4);
        const secondPart = cleaned.slice(-4);
        return `+${countryCode} ${areaCode} ${firstPart} ${secondPart}`;
      }

      // Return original if can't format
      return phoneNumber;
    };

    return (
      <div
        className={cn(
          'flex w-full flex-col gap-1',
          (channel === 'sms' || isMergedChannel(channel)) && 'gap-3',
          className
        )}
        {...props}
        ref={ref}
      >
        <div className='flex items-center gap-0'>
          <span className='max-w-[210px] truncate text-sm font-semibold'>
            {name}
          </span>
          {isVerified && (
            <span className='px-1 py-0'>
              <svg
                aria-label='Verified'
                className='x1lliihq x1n2onr6'
                fill='rgb(0, 149, 246)'
                height='18'
                role='img'
                viewBox='0 0 40 40'
                width='18'
              >
                <title>Verified</title>
                <path
                  d='M19.998 3.094 14.638 0l-2.972 5.15H5.432v6.354L0 14.64 3.094 20 0 25.359l5.432 3.137v5.905h5.975L14.638 40l5.36-3.094L25.358 40l3.232-5.6h6.162v-6.01L40 25.359 36.905 20 40 14.641l-5.248-3.03v-6.46h-6.419L25.358 0l-5.36 3.094Zm7.415 11.225 2.254 2.287-11.43 11.5-6.835-6.93 2.244-2.258 4.587 4.581 9.18-9.18Z'
                  fillRule='evenodd'
                />
              </svg>
            </span>
          )}
        </div>
        <span className='-mt-1 text-gray-500'>
          {channel === 'facebook' ? (
            <Button
              variant='link'
              size='sm'
              className='h-6 cursor-default px-0 py-0'
            >
              <img src={avatarFB} className='mr-2 aspect-square h-4 w-4' />
            </Button>
          ) : channel === 'instagram' ? (
            <Link
              to={`https://www.instagram.com/${username}`}
              target='_blank'
              className='-mt-1'
            >
              <Button variant='link' size='sm' className='h-6 px-0 py-0'>
                <img src={avatarIG} className='mr-2 aspect-square h-4 w-4' />
                {username}
              </Button>
            </Link>
          ) : channel === 'linkedin' ? (
            <Link to={linkedin_profile_link} target='_blank' className='-mt-1'>
              <Button variant='link' size='sm' className='h-6 px-0 py-0'>
                <img src={avatarLI} className='mr-2 aspect-square h-4 w-4' />
                {username}
              </Button>
            </Link>
          ) : channel === 'sms' ? (
            <div className='flex flex-col gap-1'>
              {phone && (
                <div className='flex items-center justify-start gap-2 text-sm text-muted-foreground'>
                  <PhoneIcon className='h-4 w-4' />{' '}
                  <span className='text-primary'>
                    {formatPhoneNumber(phone)}
                  </span>
                </div>
              )}
              {email && (
                <div className='flex items-center justify-start gap-2 text-sm text-muted-foreground'>
                  <MailIcon className='h-4 w-4' />{' '}
                  <span className='truncate text-primary'>{email}</span>
                </div>
              )}
            </div>
          ) : channel === 'email' ? (
            <div className='flex flex-col gap-1'>
              {email && (
                <div className='flex items-center justify-start gap-2 text-sm text-muted-foreground'>
                  <MailIcon className='h-4 w-4' />{' '}
                  <span className='truncate text-primary'>{email}</span>
                </div>
              )}
            </div>
          ) : isMergedChannel(channel) ? (
            <div className='flex flex-col gap-1'>
              {phone && (
                <div className='flex items-center justify-start gap-2 text-sm text-muted-foreground'>
                  <PhoneIcon className='h-4 w-4' />{' '}
                  <span className='text-primary'>
                    {formatPhoneNumber(phone)}
                  </span>
                </div>
              )}
              {email && (
                <div className='flex items-center justify-start gap-2 text-sm text-muted-foreground'>
                  <MailIcon className='h-4 w-4' />{' '}
                  <span className='truncate text-primary'>{email}</span>
                </div>
              )}
            </div>
          ) : null}
        </span>
        {bio && (
          <div className='break-words'>
            <p className='whitespace-pre-wrap break-words text-xs text-muted-foreground'>
              {bio}
            </p>
          </div>
        )}
        {external_url && (
          <div className='flex items-center gap-1'>
            <LinkIcon className='h-3 w-3 text-blue-500' />
            <a
              href={external_url}
              target='_blank'
              rel='noopener noreferrer'
              className='truncate text-xs text-blue-500 hover:underline'
              title={external_url}
            >
              {external_url}
            </a>
          </div>
        )}

        {(followers > 0 || following > 0) &&
          channel !== 'sms' &&
          !isMergedChannel(channel) && ( // Only show if either followers or following exists and not SMS/merged channels
            <div className='flex gap-4'>
              {followers > 0 && ( // Only show followers if it exists
                <span className='text-sm'>
                  <strong>{followers}</strong> Followers
                </span>
              )}
              {following > 0 && ( // Only show following if it exists
                <span className='text-sm'>
                  <strong>{following}</strong> Following
                </span>
              )}
            </div>
          )}
        {plan && (
          <div className='px-2'>
            <Button className='h-6 rounded-md px-2'>
              {plan.name} - ${plan.amount}
            </Button>
          </div>
        )}
      </div>
    );
  }
);
AvatarProfile.displayName = 'AvatarProfile';

export { AvatarProfile };
